export const ENTITY_HISTORY_TABLE = 'entity_history';

/**
 * The records that write to `entity_history`. Add a value here (plus a
 * metadata schema in `contracts/schemas/metadata.ts`) to onboard a new
 * entity — there is no CHECK constraint on the column, so no migration.
 */
export const ENTITY_HISTORY_ENTITY_TYPES = {
  COMPANY_PROFILE: 'COMPANY_PROFILE',
  /** A per-file style-document AI review row. `CREATED` fires when the
   *  reviewer accepts a row (result row + N style_documents). Metadata
   *  shape is defined in `contracts/schemas/metadata.ts` under the same
   *  key. */
  STYLE_DOCUMENT_AI_REVIEW: 'STYLE_DOCUMENT_AI_REVIEW',
  /** An `ai_processing_status` row (a job in the AI processing queue). One
   *  entry per status transition — started, finished/failed, resolved,
   *  reopened — never per progress tick. `entity_sub_type` is the asset type;
   *  metadata shape is defined in `contracts/schemas/metadata.ts`. */
  AI_PROCESSING_JOB: 'AI_PROCESSING_JOB',
  /** The link between an Order Hub order and the CMM record extending it.
   *  `entity_id` is the **Order Hub order id**, so the entry sits on the
   *  order's timeline. `DELETED` fires when the CMM record goes — deleted in
   *  CMM (the unlink: markers cleared, CMM documents removed from the order),
   *  or following the Hub order's own delete. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts` under the same key. */
  CMM_ORDER_LINK: 'CMM_ORDER_LINK',
  /** A user account's access being changed by an admin: blocked/unblocked
   *  (`entity_sub_type` `BLOCK`, one `isBlocked` change) or its live sessions
   *  revoked (`SESSIONS`). `entity_id` is the user; all three company ids are
   *  the user's primary company, or Retraced's own for a companyless user.
   *  Its IAM roles (`ROLE`), record access (`RECORD_ACCESS`) and company
   *  memberships (`MEMBERSHIP` `CREATED` / `DELETED`, the user added to or
   *  removed from a company)
   *  are recorded here too, with all three company ids that company.
   *  Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  USER_ACCESS: 'USER_ACCESS',
  /** A role's definition in one tenant — created, renamed, deleted, its
   *  permission grants or inheritance changed. `entity_id` is `iam_roles.id`;
   *  all three company ids are the tenant. Who holds the role is recorded on
   *  the holder's own timeline (`USER_ACCESS` / `SERVICE_ACCOUNT`), not here.
   *  Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  IAM_ROLE: 'IAM_ROLE',
  /** A service account (the identity behind a company API key): created,
   *  deleted, or its role membership changed. `entity_id` is the service
   *  account id; all three company ids are its tenant. Metadata shape is
   *  defined in `contracts/schemas/metadata.ts`. */
  SERVICE_ACCOUNT: 'SERVICE_ACCOUNT',
  /** A Social Score template and everything in it. `entity_id` is the
   *  template; `entity_sub_type` is the finding's id on a row-level change
   *  and null on a template-level one, so the admin screen's "what changed
   *  in this template" feed is a single range of the one index rather than
   *  two entity types merged at read time. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts` under the same key. */
  SOCIAL_SCORE_TEMPLATE: 'SOCIAL_SCORE_TEMPLATE',
  /** One revision of the Social Score severity methodology. Separate from
   *  `SOCIAL_SCORE_TEMPLATE` because it is a different record with a
   *  different timeline: a rule set outlives the templates graded under it,
   *  and "what changed in the methodology" is not "what changed in this
   *  library". Rule sets are immutable, so the only event is `CREATED`; a
   *  revision being superseded is recorded on the new one, which is the
   *  entry that caused it. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts` under the same key. */
  SOCIAL_SCORE_RULE_SET: 'SOCIAL_SCORE_RULE_SET',
  /** A Tracing request linked to (`CREATED`) or unlinked from (`DELETED`) an
   *  Order Hub order. `entity_id` is the hub order; `entity_sub_type` is
   *  `OWNER` or `RECEIVED`; all three company ids are the hub order's owner. */
  ORDER_HUB_TRACING_LINK: 'ORDER_HUB_TRACING_LINK',

  ORDER_HUB_ORDER: 'ORDER_HUB_ORDER',
  /** `entity_id` and all three company ids are the company whose cap changed. */
  AUTOMATION_RULE_QUOTA: 'AUTOMATION_RULE_QUOTA',
  /** One scoring of an audit against the findings library. `entity_id` is
   *  the run; `entity_sub_type` is the match's id on a row-level change and
   *  null on a run-level one, so "what happened in this run" is a single
   *  range of the one index. A run is never updated in place — re-scoring
   *  creates a new one — so the events are `CREATED` on the run and then
   *  `UPDATED`/`DELETED` on matches as reviewers correct them. That
   *  correction trail is the point: a published score that somebody changed
   *  by hand must say who, and it cannot be reconstructed afterwards.
   *  Metadata shape is defined in `contracts/schemas/metadata.ts` under the
   *  same key. */
  SOCIAL_SCORE_RUN: 'SOCIAL_SCORE_RUN',
  /** A company document archived (`ARCHIVED`) or taken back out of the
   *  archive (`RESTORED`). Archiving is per company, so `for_company_id` is
   *  the document's company and `by_company_id` / `on_behalf_of_company_id`
   *  the company whose archive changed. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  COMPANY_DOCUMENT: 'COMPANY_DOCUMENT',
  /** A feature gated behind a development tag (`active_development_tags`).
   *  `entity_id` is the `feature_id`; the table is platform-wide, so all three
   *  company ids are Retraced's own. This log replaced the table's
   *  `created_*`/`updated_*` columns: the admin features list reads its dates
   *  and last editor from here. */
  DEVELOPMENT_TAG_FEATURE: 'DEVELOPMENT_TAG_FEATURE',
  /** An `allowed_origins` row — an origin the API's CORS and SSO redirects
   *  accept. `entity_id` is the row id; the allow-list is platform-wide (any
   *  row admits its origin for every company), so all three company ids are
   *  Retraced's own and the row's `companyId` is just a recorded field. This
   *  log replaced the table's `created_*`/`updated_*` columns: the admin
   *  screens read their dates and last editor from here. */
  ALLOWED_ORIGIN: 'ALLOWED_ORIGIN',
  TRANSLATION: 'TRANSLATION',
  /** A user account's own data — created (`CREATED`, the initial name, email
   *  and language as `from: null` changes) or edited (`UPDATED`, the fields
   *  that moved). `entity_id` is the user; all three company ids are the
   *  user's primary company, or Retraced's own for a companyless user, as for
   *  `USER_ACCESS`. This log replaced the `users` table's `created_by_*` /
   *  `updated_by_*` columns; the table keeps `created_at` / `updated_at`.
   *  Deleting the user deletes these entries, since they carry the email. */
  USER_PROFILE: 'USER_PROFILE',
  /** One of a user's settings (`user_preferences`) changed. `entity_id` is
   *  the user and `entity_sub_type` the preference key; company ids as for
   *  `USER_PROFILE`. Only the keys in `USER_PREFERENCE_HISTORY_KEYS` are
   *  recorded — UI state (saved tables, filters, prompts) is not. This log
   *  replaced the table's `created_by_*` / `updated_by_*` columns. */
  USER_PREFERENCE: 'USER_PREFERENCE',
  /** A questionnaire or questionnaire template — created, deleted, or its
   *  content saved (name, description, due date, status, sections and
   *  questions). `entity_id` is the questionnaire; `entity_sub_type` is
   *  `QUESTIONNAIRE` or `TEMPLATE`; all three company ids are its owner
   *  (`company_id`). Questions have no timeline of their own: a builder save
   *  is one entry whose changes name them as `questions.<id>.<field>`. This
   *  log replaced the questions' and sections' `created_by_*` /
   *  `updated_by_*` columns. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  QUESTIONNAIRE: 'QUESTIONNAIRE',
  /** An answer request: one company asked to answer a questionnaire about
   *  another. `entity_id` is the request; `for_company_id` is the answering
   *  company, `by_company_id` the requesting brand (the row's
   *  `created_by_company_id`) and `on_behalf_of_company_id` the company the
   *  answers are about. Records the invite, status moves, due dates,
   *  reminders and the delete. This log replaced the request's actor columns
   *  except `created_by_company_id`, which is the requester rather than an
   *  audit field. Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  QUESTIONNAIRE_REQUEST: 'QUESTIONNAIRE_REQUEST',
  /** A collaborator on a shared entity — today a cascade tracing order
   *  (`entity_sub_type` `TRACING_ORDER`; CPM joins later). `entity_id` is the
   *  collaborator row; `for_company_id` the collaborating company,
   *  `by_company_id` the company that acted and `on_behalf_of_company_id` the
   *  company that invited it. Records the invite, every status move (with
   *  `metadata.trigger` when a side effect moved it), reminders and removal.
   *  Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  COLLABORATION: 'COLLABORATION',
  /** An answer to one question of an answer request, and the reviewer's
   *  verdict on it. `entity_id` is the request and `entity_sub_type` the
   *  question, so a request's answers and one answer's history are both one
   *  index range; company ids as for `QUESTIONNAIRE_REQUEST`. Kept off the
   *  request's own timeline so answering does not change the request's "last
   *  updated by". This log replaced the answers' actor columns. Metadata shape
   *  is defined in `contracts/schemas/metadata.ts`. */
  QUESTIONNAIRE_ANSWER: 'QUESTIONNAIRE_ANSWER',
  /** A comment on one question of an answer request: written, edited,
   *  deleted. `entity_id` is the request and `entity_sub_type` the comment, so
   *  a request's comments (deleted ones included) are one index range;
   *  company ids as for `QUESTIONNAIRE_REQUEST`. The comment row keeps its
   *  author, which gates editing it; this log adds who edited and deleted it
   *  and the text they replaced. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  QUESTIONNAIRE_COMMENT: 'QUESTIONNAIRE_COMMENT',
  /** A user's address taken off the SES account-level suppression list
   *  (`DELETED`), lifted automatically when they ask for a login code so the
   *  code can reach a mailbox that bounced or complained before.
   *  `entity_id` is the user, `entity_sub_type` the suppression reason
   *  (`BOUNCE` / `COMPLAINT`); company ids as for `USER_PROFILE`. No actor:
   *  the requester has not proven they own the address yet. Metadata shape
   *  is defined in `contracts/schemas/metadata.ts`. */
  USER_EMAIL_SUPPRESSION: 'USER_EMAIL_SUPPRESSION',
  /** A risk assessment blueprint (`risk_analysis_service_assessment_blueprints`)
   *  — created, edited (name, goal, tags, type, assessment formula) or
   *  archived. `entity_id` is the blueprint; all three company ids are its
   *  owner (`created_by_company_id`). The standard blueprint the calculation
   *  creates for every company is not recorded: no person creates it. The row
   *  keeps its creator, which the assessment-mitigation step reads. Metadata
   *  shape is defined in `contracts/schemas/metadata.ts`. */
  ASSESSMENT_BLUEPRINT: 'ASSESSMENT_BLUEPRINT',
  /** A risk rule template (`risk_analysis_service_rule_templates`) with its
   *  rules — created, copied, edited or deleted. `entity_id` is the template;
   *  all three company ids are its owner (`created_by_company_id`). Rules have
   *  no timeline of their own: a save is one entry whose changes name them as
   *  `rules.<id>[.<part>]`. The template lists read their creator and last
   *  editor from here. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  RULE_TEMPLATE: 'RULE_TEMPLATE',
  /** A risk of the global risk catalogue (`risk_analysis_service_risks`) —
   *  created or edited by Retraced staff, with its impact area links.
   *  `entity_id` is the risk; the catalogue is platform-wide, so all three
   *  company ids are Retraced's own. The risk reads take their creator and
   *  last editor from here. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  RISK: 'RISK',
  /** A certification rule (`rules`): the requirement expression claims
   *  validation evaluates for a certification — created, replaced or deleted
   *  by Retraced staff. `entity_id` is the rule; the table is platform-wide,
   *  so all three company ids are Retraced's own. This log replaced the
   *  `rules_history` snapshots and the row's actor columns: the admin list
   *  reads its creator and last editor from here. Metadata shape is defined
   *  in `contracts/schemas/metadata.ts`. */
  CERTIFICATION_RULE: 'CERTIFICATION_RULE',
  /** A CAPA action or plan archived (`ARCHIVED`) or taken back out of the
   *  archive (`RESTORED`) by one company. Archiving is per company and
   *  private to it, so all three company ids are the archiving company;
   *  `entity_id` is the action. This log replaced the archive row's actor
   *  columns, and keeps what unarchiving deletes. Metadata shape is defined
   *  in `contracts/schemas/metadata.ts`. */
  CAPA_ACTION: 'CAPA_ACTION',
  /** A document type (`document_types`): the catalogue every company,
   *  product and tracing document is classified against — created, edited,
   *  deprecated or restored, or deleted by Retraced staff. `entity_id` is the
   *  type; the catalogue is platform-wide, so all three company ids are
   *  Retraced's own. This log replaced the row's actor columns: the admin
   *  list and detail read their creator and last editor from here. Metadata
   *  shape is defined in `contracts/schemas/metadata.ts`. */
  DOCUMENT_TYPE: 'DOCUMENT_TYPE',
  /** An audit type of the global audit catalogue
   *  (`audits_service_audit_types`) — created, edited, deprecated / brought
   *  back (an `isDeprecated` change) or deleted by Retraced staff.
   *  `entity_id` is the audit type; the catalogue is platform-wide, so all
   *  three company ids are Retraced's own. This log replaced the table's
   *  `created_by_*` / `updated_by_*` columns: the admin reads take their
   *  creator and last editor from here. The table keeps `created_at` /
   *  `updated_at`. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  AUDIT_TYPE: 'AUDIT_TYPE',
  /** An audit (`audits_service_audits`) — uploaded (by a user, the AI upload
   *  flow, or the amfori sync), edited, or deleted when an amfori supplier is
   *  unlinked. `entity_id` is the audit; `for_company_id` is the audited
   *  company, `by_company_id` and `on_behalf_of_company_id` the uploader that
   *  owns the record, so an edit by a company it was shared with still lands
   *  on the owner's timeline. This log replaced the table's `created_by_*` /
   *  `updated_by_*` columns, except `created_by_company_id`, which the amfori
   *  sync reads as the pulling brand. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  AUDIT: 'AUDIT',
  /** A finding (`audits_service_audit_findings`) — on an audit, an SLCP
   *  assessment, a factory visit or anything else CAPA tracks — created (by a
   *  user, AI extraction, the CAPA import, the amfori sync or the SLCP job),
   *  edited, removed (`isRemoved`), or deleted when an amfori supplier is
   *  unlinked. `entity_id` is the finding; `for_company_id` is the responsible
   *  company (`company_id`), `by_company_id` and `on_behalf_of_company_id`
   *  the creator company, which owns it and decides who may edit it. This log
   *  replaced the table's actor columns, except `created_by_company_id`, which
   *  visibility and edit rights read. Also a finding archived (`ARCHIVED`) or
   *  taken back out of the archive (`RESTORED`) by one company: archiving is
   *  per company and private to it, so on those entries all three company ids
   *  are the archiving company. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  AUDIT_FINDING: 'AUDIT_FINDING',
  /** A webshop (`webshops`): a shop id a brand embeds on its storefront,
   *  which authentication resolves to the company it is attached to —
   *  created, edited (renamed, blocked or unblocked, moved to another company,
   *  its connected origins changed) or deleted by Retraced staff. `entity_id`
   *  is the shop id; a rename is recorded on the new id, as an `id` change.
   *  The roster is platform-wide, so all three company ids are Retraced's own
   *  and the webshop's `companyId` is a recorded field. This log replaced the
   *  row's actor columns: the admin screens read their creator and last
   *  editor from here. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  WEBSHOP: 'WEBSHOP',
  /** A style, variant or SKU (`styles`; `entity_sub_type` `STYLE` /
   *  `VARIANT` / `SKU`) — created, edited, bulk-edited or bulk-imported,
   *  archived or restored by a customer. `entity_id` is the row; all three
   *  company ids are its owner (`for_company_id`). This log replaced the
   *  per-feature `products_audit_logs` table. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  STYLE: 'STYLE',
  /** A platform feature of the global catalogue (`platform_features`) —
   *  renamed, re-described, or switched on or off for the whole platform by
   *  Retraced staff. Features are created and dropped by migrations, which
   *  record nothing. `entity_id` is the feature; the catalogue is
   *  platform-wide, so all three company ids are Retraced's own. Metadata
   *  shape is defined in `contracts/schemas/metadata.ts`. */
  PLATFORM_FEATURE: 'PLATFORM_FEATURE',
  /** A company's grant of a platform feature (`companies_platform_features`)
   *  — given (`CREATED`), or switched off and on again (`UPDATED`, an
   *  `isActive` change). `entity_id` is the company and `entity_sub_type` the
   *  feature; all three company ids are the company. Granted by staff (source
   *  `ADMIN`), as a side effect of a customer inviting a collaborator (`WEB`),
   *  or by the cascade-tracing order sync (`SYSTEM`). Metadata shape is
   *  defined in `contracts/schemas/metadata.ts`. */
  COMPANY_FEATURE: 'COMPANY_FEATURE',
  /** A generated tag identification (`generated_tag_identifications`) — the
   *  URL a printed QR code encodes — changed by Retraced staff from the admin
   *  tag identifications page: its scans redirected elsewhere or back
   *  (`redirectionUrl`), or its tag unlinked from the asset (`tagId`,
   *  `tracingAssetId`, `tracingAssetType` to null). Always `UPDATED`, source
   *  `ADMIN`. Creating one (the QR code jobs, millions a quarter) records
   *  nothing. `entity_id` is the tag identification; all three company ids
   *  are the company it belongs to. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  TAG_IDENTIFICATION: 'TAG_IDENTIFICATION',
  /** A facility process of the global catalogue (`facility_processes`) —
   *  created, renamed, moved to another production process or re-translated
   *  by Retraced staff from the admin page. Processes added by migrations
   *  record nothing. `entity_id` is the process; the catalogue is
   *  platform-wide, so all three company ids are Retraced's own. Metadata
   *  shape is defined in `contracts/schemas/metadata.ts`. */
  FACILITY_PROCESS: 'FACILITY_PROCESS',
  /** A company's request for access to another company's audit
   *  (`audits_service_audit_access_requests`) — filed (`REQUESTED`, or
   *  `APPROVED` straight away on a public audit), withdrawn by the requester
   *  (`CANCELED`), or answered by a share of the audit (`APPROVED`, with the
   *  sharer as the actor). `entity_id` is the request; all three company ids
   *  are the company the access is for (`requested_for_company_id`), whose
   *  request it is, and `metadata.auditId` names the audit. This log replaced
   *  the table's actor columns. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  AUDIT_ACCESS_REQUEST: 'AUDIT_ACCESS_REQUEST',
  /** A product data point (`product_data_points`) changed by Retraced staff
   *  from `/management/product-data-points`: moved to another group or
   *  position (`groupId`, `sortOrder`), or hidden / shown (`isHidden`). Always
   *  `UPDATED`, source `ADMIN`; data points are code, so none is created or
   *  deleted here. Its name is a `TRANSLATION` entry. `entity_id` is the data
   *  point; the catalogue is platform-wide, so all three company ids are
   *  Retraced's own. Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  PRODUCT_DATA_POINT: 'PRODUCT_DATA_POINT',
  /** A product data point group (`product_data_point_groups`) created,
   *  reordered, hidden / shown or deleted by Retraced staff from
   *  `/management/product-data-points`. Its name is a `TRANSLATION` entry.
   *  `entity_id` is the group; all three company ids are Retraced's own.
   *  Metadata shape is defined in `contracts/schemas/metadata.ts`. */
  PRODUCT_DATA_POINT_GROUP: 'PRODUCT_DATA_POINT_GROUP',
  /** An admin merge of a duplicate (source) company into a surviving
   *  (target) one. `entity_id` is `company_merges.id`; all three company ids
   *  are the target, which is the company that remains. `CREATED` when the
   *  merge is requested, then one `UPDATED` per status transition (a
   *  `status` change, plus the failed group on a failure). Metadata shape is
   *  defined in `contracts/schemas/metadata.ts`. */
  COMPANY_MERGE: 'COMPANY_MERGE',
  /** A supplier's amfori link — its `amfori_site_id` and
   *  `amfori_business_partner_id` answers — first written by the amfori sync
   *  (`CREATED`, source `IMPORT`, the syncing brand as the actor), set by hand
   *  (`UPDATED`), or cleared (`UNLINKED`, from the supplier panel or the sync
   *  report's unlink, which also deletes the synced audits). `entity_id` is
   *  the supplier; the link is a self-reported answer every brand shares, so
   *  all three company ids are the supplier and the brand is the actor's
   *  company. The weekly `amfori_last_synced_at` bump records nothing, nor do
   *  the amfori credentials. Metadata shape is defined in
   *  `contracts/schemas/metadata.ts`. */
  AMFORI_SUPPLIER_LINK: 'AMFORI_SUPPLIER_LINK',
  /** A brand's amfori sync warning about one site, dismissed into the brand's
   *  archive (`ARCHIVED`, source `WEB`), taken back out by a user
   *  (`RESTORED`, `WEB`), or brought back by a later sync that found the
   *  site's amfori audit count or its matched companies changed (`RESTORED`,
   *  source `SYSTEM`, no actor, with the reason). `entity_id` is the amfori
   *  Site ID and `entity_sub_type` the warning code; the archive is the
   *  brand's own, so all three company ids are the brand. Metadata shape is
   *  defined in `contracts/schemas/metadata.ts`. */
  AMFORI_SYNC_WARNING: 'AMFORI_SYNC_WARNING',
} as const;

/**
 * The ownership context of an entity no tenant owns.
 *
 * The three company columns are NOT NULL because almost every entity belongs
 * to somebody, but a few are the platform's own — a Social Score template is
 * one revision of a Retraced methodology, not a customer's record. Writing
 * the acting admin's company there instead would scope the log to whichever
 * admin tenant happened to make the change, and the next admin would read an
 * empty history.
 *
 * Not a company id and not an FK — nothing joins these columns.
 */
export const ENTITY_HISTORY_PLATFORM_SCOPE = 'PLATFORM';

/** `companyIds` for a platform-owned entity. */
export const ENTITY_HISTORY_PLATFORM_COMPANY_IDS = {
  forCompanyId: ENTITY_HISTORY_PLATFORM_SCOPE,
  byCompanyId: ENTITY_HISTORY_PLATFORM_SCOPE,
  onBehalfOfCompanyId: ENTITY_HISTORY_PLATFORM_SCOPE,
} as const;

export type EntityHistoryEntityType =
  (typeof ENTITY_HISTORY_ENTITY_TYPES)[keyof typeof ENTITY_HISTORY_ENTITY_TYPES];

export const ENTITY_HISTORY_EVENT_TYPES = {
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  DELETED: 'DELETED',
  /**
   * A change to data the entity owns rather than to one viewer's copy of it —
   * recorded once, on the entity's own timeline, and read by every viewer
   * through `sharedScope`. For company profiles this is a GLOBAL data point.
   */
  GLOBAL_UPDATED: 'GLOBAL_UPDATED',
  /** A `COMPANY_DOCUMENT` put in one company's archive, or a `STYLE`
   *  archived. */
  ARCHIVED: 'ARCHIVED',
  /** A `COMPANY_DOCUMENT` taken back out of one company's archive, or a
   *  `STYLE` unarchived. */
  RESTORED: 'RESTORED',
  /** An `ORDER_HUB_ORDER` whose lines were sent to Tracing. */
  SENT_TO_TRACING: 'SENT_TO_TRACING',
  /** An `ORDER_HUB_ORDER` whose lines were freed from their Tracing orders. */
  REMOVED_FROM_TRACING: 'REMOVED_FROM_TRACING',
  /** An `AMFORI_SUPPLIER_LINK` whose amfori Site ID was cleared. */
  UNLINKED: 'UNLINKED',
} as const;

export type EntityHistoryEventType =
  (typeof ENTITY_HISTORY_EVENT_TYPES)[keyof typeof ENTITY_HISTORY_EVENT_TYPES];

/** Where the change came from — what the UI renders as "Source: …". */
export const ENTITY_HISTORY_SOURCES = {
  WEB: 'WEB',
  ADMIN: 'ADMIN',
  PUBLIC_API: 'PUBLIC_API',
  QUESTIONNAIRE_SYNC: 'QUESTIONNAIRE_SYNC',
  IMPORT: 'IMPORT',
  SYSTEM: 'SYSTEM',
  INTERNAL_API: 'INTERNAL_API',
} as const;

export type EntityHistorySource =
  (typeof ENTITY_HISTORY_SOURCES)[keyof typeof ENTITY_HISTORY_SOURCES];

export const ENTITY_HISTORY_DEFAULT_PAGE_SIZE = 20;
export const ENTITY_HISTORY_MAX_PAGE_SIZE = 100;
